// eslint-disable-next-line import/extensions
import "bpmn-js-bpmnlint/dist/assets/css/bpmn-js-bpmnlint.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
import "bpmn-js/dist/assets/bpmn-js.css";
import "bpmn-js/dist/assets/diagram-js.css";
import "diagram-js-minimap/assets/diagram-js-minimap.css";
import {
  CommonHelperFunctions,
  ESdChangeReason,
  IWFNode,
  IWFNodes,
  IWorkFlow,
  KeyCombinations
} from "@kuika/kuika-cl-model";
import type { SaveXMLOptions, SaveXMLResult } from "bpmn-js/lib/BaseViewer";
import { is } from "bpmn-js/lib/util/ModelUtil";
import * as React from "react";
import { isWorkflowCollabEnabled } from "../../../services/collab/collab-feature-gate";
import { COLLAB_CLIENT_ID } from "../../../services/collab/collab-session-core";
import { WorkflowSyncService } from "../../../services/workflow-sync.service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { isInsideModalOverlay } from "../../../shared/utility/modal-keyboard-scope";
import {
  clearModelerBuildFailure,
  recordModelerBuildFailure
} from "../../../shared/utility/workflow-modeler-diagnostics";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaInfoBox from "../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaSmallSpinner from "../../ui-kit/kuika-small-spinner/kuika-small-spinner";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaText from "../../ui-kit/typography/Text/Text";
import { CreateWorkflow } from "./create-workflow";
import DiagramPalette from "./diagram-palette";
import { healedPoolBounds, IPoolLaneRect } from "./pool-lane-geometry";
import ProcessEditor from "./process-editor/process-editor";
import {
  IDLE_WORKFLOW_HISTORY,
  publishWorkflowHistoryState,
  subscribeWorkflowHistoryRequests,
  WorkflowHistoryDirection
} from "./process-editor/workflow-history-bus";
import { decorateRuntimeLintTip } from "./runtime-lint-tip";
import { installWorkflowCollabOverlay, IWfCollabOverlayHandle } from "./workflow-collab-overlay";

export interface IWorkFlowWindowState {
  miniMapVisible: boolean;
  isDiagramLoading: boolean;
  dragIsAvtive: boolean;
  lintErrors: number;
  lintWarnings: number;
  lintingActive: boolean;
}

export interface IWorkFlowWindowProps {}

// ---- narrow structural views for the KOA-18258 pool/lane heal (no bpmn-js types in scope) ----

interface IHealRegistryShape {
  type?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  di?: IHealShapeDi;
}

interface IHealShapeDi {
  isHorizontal?: boolean;
}

interface IHealElementRegistry {
  filter(predicate: (element: IHealRegistryShape) => boolean): IHealRegistryShape[];
}

interface IHealModeling {
  resizeShape(shape: IHealRegistryShape, bounds: IPoolLaneRect): void;
}

interface IHealModelerView {
  __wfCollabImportedWfId?: string;
  /** Per-node snapshot cache keyed by diagram element id, used to restore workflow-model-only
   * data (extra parameters, assignments, etc.) that a diagram undo/redo/import doesn't carry —
   * see rebuildWorkflowDataFromDiagram below. */
  __fullDataCache?: Map<string, Partial<IWFNode>>;
  get(name: string): unknown;
  saveXML(options?: SaveXMLOptions): Promise<SaveXMLResult>;
}

/** The registry shape's rect, or null when any dimension is missing (imports never are). */
function healRectOf(shape: IHealRegistryShape): IPoolLaneRect | null {
  if (
    typeof shape.x !== "number" ||
    typeof shape.y !== "number" ||
    typeof shape.width !== "number" ||
    typeof shape.height !== "number"
  ) {
    return null;
  }
  return { x: shape.x, y: shape.y, width: shape.width, height: shape.height };
}

// ---- narrow structural views for canvas history + diagram-data rebuild (stepCanvasHistory,
// publishCanvasHistoryState, rebuildWorkflowDataFromDiagram) — bpmn-js's own runtime-service types
// aren't usable either (e.g. Modeling.updateProperties takes `properties: any`, and
// ElementRegistry/EventBus/Selection/CommandStack/EditorActions ship no types at all), so these
// mirror the pool/lane heal views above: narrow, precise, and scoped to what's actually called.

interface IWfBusinessObject {
  $type?: string;
  name?: string;
}

interface IWfDiagramElement {
  id: string;
  type?: string;
  businessObject?: IWfBusinessObject;
}

interface IWfElementRegistry {
  getAll(): IWfDiagramElement[];
}

interface IWfEventBus {
  fire<TPayload>(event: string, payload: TPayload): void;
}

interface IWfSelection {
  get(): IWfDiagramElement[];
}

interface IWfElementPropertyUpdate {
  name?: string;
}

interface IWfModeling {
  updateProperties(element: IWfDiagramElement, properties: IWfElementPropertyUpdate): void;
}

interface IWfEditorActions {
  trigger(action: string): void;
}

interface IWfCommandStack {
  canUndo(): boolean;
  canRedo(): boolean;
}

declare let window: Window & { userInfo: { username: string } };

class WorkFlowWindow extends PureSdComponent<IWorkFlowWindowProps & IWithSDProps, IWorkFlowWindowState> {
  modelerRef = React.createRef<HTMLDivElement>();

  modeler: any | undefined = undefined;

  /** Guards against two overlapping async modeler builds (self-heal + id-change). */
  private buildingModeler: boolean = false;

  currentUser: string | undefined = window.userInfo?.username;

  workflowSyncService = new WorkflowSyncService();

  disposeReposition?: () => void;

  disposeLintStatusOverlay?: () => void;

  disposeEntryTooltips?: () => void;

  disposeContextPadAutoClose?: () => void;

  collabOverlay?: IWfCollabOverlayHandle;

  /** Unsubscribes the sub-bar's undo/redo buttons from this canvas. */
  private disposeHistoryRequests?: () => void;

  // Serializes modeler create/import work so re-entrant componentDidUpdate calls
  // never run prepareModeller/importXML concurrently (concurrent imports throw
  // bpmn-js "plane ... already exists" from diagram-js Canvas.createPlane).
  private modelerOpLock: Promise<void> = Promise.resolve();

  constructor(props: IWorkFlowWindowProps & IWithSDProps) {
    super(props);
    this.state = {
      miniMapVisible: false,
      dragIsAvtive: false,
      isDiagramLoading: true,
      lintErrors: 0,
      lintWarnings: 0,
      lintingActive: true
    };
  }

  async componentDidMount() {
    // Returning to the process-automation tool via the footer toggle (raw `setDashboardState`)
    // does NOT re-select the last process — it was cleared on leave (`navigateToDesignView` →
    // `clearSelectedWf`) to release the advisory lock. Only the programmatic navigations re-select,
    // so a plain toggle stranded the user on an empty view until they re-picked the process. Restore
    // it here so EVERY entry path reopens the last process; `setSelectedWf` dedupes if a navigation
    // already kicked one off, and componentDidUpdate builds the modeler once the selection lands.
    // Skip when the create-process gallery is up so we don't override an in-progress "new process".
    this.restoreLastProcessIfNeeded();

    // The sub-bar's history buttons sit outside this subtree; the bus lets them drive the
    // same step the keyboard does, and lets this canvas tell them what is steppable.
    this.disposeHistoryRequests = subscribeWorkflowHistoryRequests((direction: WorkflowHistoryDirection): void => {
      void this.stepCanvasHistory(direction);
    });

    // Only default to the create screen when there is no last-open workflow to restore. When there is
    // one, the entry-path handler (toolbar/footer extraSetup, redirectToWorkflow, keyboard shortcut)
    // re-selects it and componentDidUpdate initializes the modeler. Forcing create mode here on every
    // remount would flash the create screen for the duration of that async load before the editor shows.
    if (!this.props.sd.lastOpenWfId) {
      this.props.sd.setCreatingWfActive(true);
    }

    // A workflow can already be selected BEFORE this canvas mounts — e.g. a
    // chat-driven create_workflow (openAfterCreate) sets selectedWf while the
    // user is on the Designer (screen) tab, then switches to the
    // process-automation view, so WorkFlowWindow mounts into an already-set
    // selectedWf. componentDidUpdate only builds the modeler when selectedWf.id
    // CHANGES, so that path never fires here (prevProps already had the id) and
    // the modeler would never be built — window.bpmnModeler stays undefined and
    // every DSL edit command fails with "BPMN Modeler is not ready". Build it
    // on mount when a workflow is already selected. (KOA-17362)
    //
    // ON THE OP LOCK: this used to run un-serialized, racing componentDidUpdate's
    // reconcileModeler — two concurrent importXML (the KOA-18065 crash class the
    // lock exists for) and a DUPLICATE collab join, because both import paths hit
    // the canvas-ready join. Serialized, whichever path imports first stamps
    // selectedWfId and the other skips.
    //
    // ON THE CATCH: the build runs inside an ASYNC lifecycle method, so an
    // uncaught throw is an unhandled rejection React never surfaces and the canvas
    // dies silently with the "Loading…" overlay up forever (KOA-18136). Recording
    // it INSIDE the lock — rather than leaning on the chain's trailing `.catch` —
    // is what carries the cause into the DSL abort message and drops the overlay;
    // the trailing catch only keeps the lock chain alive for the next operation.
    this.modelerOpLock = this.modelerOpLock
      .then(async () => {
        try {
          const built: boolean = await this.ensureModelerBuilt();
          if (built) {
            await this.updateWorkFlow();
            this.setState({ isDiagramLoading: false });
          }
        } catch (error: unknown) {
          recordModelerBuildFailure(error, this.props.sd.selectedWf?.id);
          this.setState({ isDiagramLoading: false });
        }
      })
      .catch(() => undefined);
  }

  /**
   * Build the BPMN modeler for the currently-selected workflow when one is
   * selected, the container is mounted, and no modeler exists yet. Shared by
   * componentDidMount and componentDidUpdate so a missed mount-time build (the
   * cold first-run race where create_workflow's setSelectedWf lands around the
   * mount and neither the mount nor an id-change build fires) SELF-HEALS on the
   * next render instead of stranding the turn with "BPMN Modeler is not ready"
   * (KOA-17362). Concurrency-guarded so overlapping updates never start two
   * builds. Returns true when it built the modeler this call.
   */
  /**
   * Reopen the last process if a programmatic leave (`navigateToDesignView` → `clearSelectedWf`, which
   * releases the advisory lock) cleared the selection. The plain design↔workflow toggle keeps
   * `selectedWf`, so this is a no-op there. `setSelectedWf` dedupes a concurrent restore; skipped while
   * the create-process gallery is up so an in-progress "new process" isn't overridden.
   */
  private restoreLastProcessIfNeeded = (): void => {
    if (!this.props.sd.selectedWf && this.props.sd.lastOpenWfId && !this.props.sd.isCreateWfActive) {
      void this.props.sd.setSelectedWf(this.props.sd.lastOpenWfId);
    }
  };

  private ensureModelerBuilt = async (): Promise<boolean> => {
    const selectedWf: IWorkFlow | undefined = this.props.sd.selectedWf;
    if (!selectedWf || !this.modelerRef.current || this.buildingModeler) return false;

    // DESYNC REPAIR. `resetModeler()` destroys the instance without clearing this field
    // (clearSelectedWf, and any teardown while this window stays mounted), so a stale
    // `this.modeler` made the component believe it still had a canvas and never rebuild.
    // The source of truth is the service that owns the instance — NOT `window.bpmnModeler`,
    // which only the editable path publishes: on a locked workflow `createViewer` builds a
    // NavigatedViewer and sets `window.viewer` instead, so the global read reported every
    // read-only canvas as dead and rebuilt it on every update, forever.
    const liveModeler: unknown = this.props.sd.getModeler();
    if (this.modeler && this.modeler !== liveModeler) this.modeler = undefined;
    if (this.modeler) return false;

    this.buildingModeler = true;
    try {
      const readOnly: boolean = this.computeReadOnly(selectedWf);
      this.props.sd.setSelectedWfReadOnly(readOnly);
      this.setState({ isDiagramLoading: true });
      this.modeler = await this.prepareModeller(readOnly);
      if (this.modeler) {
        clearModelerBuildFailure();
      } else {
        // prepareModeller returns undefined (not throws) when the container ref is
        // gone — record it, or the abort path blames a mount race that already ended.
        recordModelerBuildFailure("modeler container was not mounted (modelerRef empty)", selectedWf.id);
      }
      return !!this.modeler;
    } catch (error: unknown) {
      recordModelerBuildFailure(error, selectedWf.id);
      throw error;
    } finally {
      this.buildingModeler = false;
    }
  };

  private computeReadOnly = (selectedWf: IWorkFlow | undefined): boolean => {
    if (isWorkflowCollabEnabled()) {
      // Collab mode retires the advisory lock: everyone edits, the op stream converges.
      return false;
    }
    let readOnly: boolean = false;
    if (selectedWf && !(selectedWf.userInUse === undefined || selectedWf.userInUse === null)) {
      readOnly = selectedWf.userInUse !== this.currentUser;
    }
    return readOnly;
  };

  componentWillUnmount(): void {
    window.removeEventListener("keydown", this.handlePlatformKeyCombinations);
    this.disposeHistoryRequests?.();
    this.disposeHistoryRequests = undefined;
    // A retained "can undo" would outlive the diagram it described and light the buttons up
    // over the next empty canvas.
    publishWorkflowHistoryState(IDLE_WORKFLOW_HISTORY);
    try {
      this.disposeReposition?.();
    } catch {}
    try {
      this.disposeLintStatusOverlay?.();
    } catch {}
    try {
      this.uninstallDiagramJsTooltipBridge(this.modeler);
    } catch {}
    try {
      this.disposeEntryTooltips?.();
    } catch {}
    try {
      this.disposeContextPadAutoClose?.();
    } catch {}
    try {
      this.collabOverlay?.dispose();
      this.collabOverlay = undefined;
    } catch {}
    // Destroy the modeler on unmount so the flows editor doesn't leak its
    // bpmn-js graph + document.body keyboard binding when the designer view is
    // left. Re-entering the flows view re-selects a workflow, which recreates
    // the modeler via componentDidUpdate.
    try {
      this.props.sd.resetModeler();
    } catch {}
    this.modeler = undefined;
  }

  /** Presence decorations (remote selections + live cursors) for the collab session. */
  installCollabOverlay = (container: HTMLElement): void => {
    try {
      this.collabOverlay?.dispose();
      this.collabOverlay = installWorkflowCollabOverlay(this.modeler, container, {
        ownClientId: COLLAB_CLIENT_ID,
        onCursorMoved: (x: number, y: number): void => this.props.sd.onWorkflowCursorMoved(x, y)
      });
      this.collabOverlay.updateRoster(this.props.sd.workflowCollabRoster);
    } catch (error) {
      // Decoration-only — presence must never take the editor down.
      console.error("Workflow collab overlay install failed:", error);
    }
  };

  componentDidUpdate = (prevProps: IWorkFlowWindowProps & IWithSDProps) => {
    // Presence roster changed → re-render remote selections/cursors (cheap diff
    // inside). Decoration-only, so it runs OUTSIDE the modeler op lock — it must
    // not wait behind a slow import.
    if (this.collabOverlay && this.props.sd.workflowCollabRoster !== prevProps.sd.workflowCollabRoster) {
      this.collabOverlay.updateRoster(this.props.sd.workflowCollabRoster);
    }

    // KOA-18199: the lint rules read project.processStepScreenIds + screenList, but the
    // linter only re-runs on DIAGRAM events — a screen delete (which arrives as a
    // screenList/project state refresh) never re-linted, so the "assigned screen no
    // longer exists" warning stayed hidden until the next canvas gesture. Cheap in-place
    // re-evaluation; lint refresh is decoration and must never take the editor down.
    if (
      this.modeler &&
      (this.props.sd.screenList !== prevProps.sd.screenList || this.props.sd.project !== prevProps.sd.project)
    ) {
      try {
        (this.modeler as any).get?.("linting", false)?.update?.();
      } catch {
        // decoration-only
      }
    }

    // Queue the reconciliation behind any in-flight one. componentDidUpdate is
    // async and re-enters on every setState({ isDiagramLoading }); without this
    // serialization a second prepareModeller/importXML could run while the first
    // is still awaiting — importing into a half-swapped/destroyed modeler and
    // throwing bpmn-js "plane ... already exists" (KOA-18065 test feedback).
    this.modelerOpLock = this.modelerOpLock.then(() => this.reconcileModeler(prevProps)).catch(() => undefined);
  };

  private reconcileModeler = async (prevProps: IWorkFlowWindowProps & IWithSDProps): Promise<void> => {
    const selectedWf = this.props.sd.selectedWf;
    if (selectedWf && selectedWf.id !== prevProps.sd.selectedWf?.id) {
      // Switched to a different workflow — drop the old modeler so it rebuilds
      // for the new selection below.
      this.props.sd.setCreatingWfActive(false);
      this.modeler = undefined;
    }

    // Build when a workflow is selected but the modeler isn't ready — covers a
    // fresh id-change AND a missed mount-time build (cold first-run self-heal).
    // updateWorkFlow (via wfIdChanged below) imports the diagram after a fresh
    // build, since prepareModeller leaves selectedWfId unset.
    await this.ensureModelerBuilt();

    const wfIdChanged = this.modeler && selectedWf && (this.modeler as any).selectedWfId !== selectedWf.id;
    const wfXmlChanged =
      this.modeler &&
      selectedWf &&
      prevProps.sd.selectedWf &&
      selectedWf.id === prevProps.sd.selectedWf.id &&
      selectedWf.xml !== prevProps.sd.selectedWf.xml;

    if (wfIdChanged || wfXmlChanged) {
      this.setState({ isDiagramLoading: true });
      await this.updateWorkFlow();
      this.setState({ isDiagramLoading: false });
    }
  };

  prepareModeller = async (readonly: boolean) => {
    if (!this.modelerRef.current) return;

    try {
      this.disposeReposition?.();
    } catch {}

    try {
      this.disposeLintStatusOverlay?.();
    } catch {}

    try {
      this.disposeContextPadAutoClose?.();
    } catch {}

    window.removeEventListener("keydown", this.handlePlatformKeyCombinations);
    this.modelerRef.current.innerHTML = "";

    const modeler = await this.props.sd.createModeler(this.modelerRef.current, readonly);

    this.installDiagramJsTooltipBridge(modeler);
    this.installEntryTooltips(modeler);
    this.disposeContextPadAutoClose = this.installContextPadAutoClose(modeler);

    try {
      const m: any = modeler;
      const linting = m.get("linting");
      const eventBus = m.get("eventBus");

      const isActive = linting && typeof linting.isActive === "function" ? !!linting.isActive() : true;
      this.setState({ lintingActive: isActive });

      (m as any).__kuika_lint_forced_off = !isActive;

      eventBus.on("linting.completed", (e: any) => {
        const forcedOff = !!(m as any).__kuika_lint_forced_off;
        const container: HTMLElement | undefined = (m as any)._container;

        if (forcedOff) {
          try {
            if (container) {
              container.querySelectorAll<HTMLElement>(".djs-overlay.djs-overlay-linting").forEach((el) => el.remove());
            }
          } catch {}

          this.setState({ lintErrors: 0, lintWarnings: 0 });
          return;
        }

        const issues = (e && e.issues) || {};
        let errs = 0;
        let warns = 0;

        Object.keys(issues).forEach((id) => {
          (issues[id] || []).forEach((r: any) => {
            if (r.category === "error") errs++;
            else if (r.category === "warn") warns++;
          });
        });

        this.setState({
          lintErrors: errs,
          lintWarnings: warns
        });
      });
    } catch {}

    try {
      this.patchLabelEditingBBox(modeler);
      this.patchHideUserIconWhileEditing(modeler);
    } catch {}
    this.disposeReposition = this.attachDirectEditingRepositionOnZoom(modeler);
    this.disposeLintStatusOverlay = this.removeOldLintStatusOverlay(modeler);

    this.props.sd.getProjectsWithExternalWorkflowData();

    if (!readonly) {
      window.addEventListener("keydown", this.handlePlatformKeyCombinations);
    }

    this.modeler = modeler;
    return modeler;
  };

  // NOTE: the old removeUnWantedBpmnTools/hideElementsWithClassName DOM-sweep (hiding stock
  // palette entries by class) was removed — the stock .djs-palette is display:none entirely,
  // replaced by the Kuika category-rail palette (diagram-palette.tsx).

  updateWorkFlow = async () => {
    const selectedWf = this.props.sd.selectedWf;

    if (selectedWf !== undefined && this.modeler) {
      (this.modeler as any).selectedWfId = selectedWf.id;
      if (selectedWf?.xml) {
        try {
          await this.modeler.importXML(selectedWf.xml);

          const m: any = this.modeler;
          const eventBus = m.get("eventBus");
          const elementRegistry = m.get("elementRegistry");

          const allEls = elementRegistry.getAll ? elementRegistry.getAll() : [];
          // console.log("[lint] updateWorkFlow -> elements.changed fire, elCount=", allEls.length);
          eventBus.fire("elements.changed", { elements: allEls });

          // History feed for the sub-bar's buttons. Bound here rather than lazily with the
          // key handler's data listeners, which only attach after the first keypress — the
          // buttons must be honest from the moment the diagram is on screen. The freshly
          // imported stack is empty, so this first publish is also what resets them between
          // processes.
          if (!m.__kuikaHistoryListenerAttached) {
            eventBus.on("commandStack.changed", (): void => this.publishCanvasHistoryState());
            m.__kuikaHistoryListenerAttached = true;
          }
          this.publishCanvasHistoryState();
          try {
            (this.props.sd.getModeler() as any)?.get("linting")?.update?.();
          } catch {}

          const container = (this.modeler as any)._container;
          container.style.pointerEvents = "all";
          container.style.opacity = 1;
          this.props.sd.setCreatingWfActive(false);

          // Canvas-ready stamp for workflow collab: the extractor/join seed must only read
          // a registry that shows THIS workflow. Join here (idempotent for the same id) —
          // the setSelectedWf-time attempt is skipped while the canvas is still importing.
          (this.modeler as any).__wfCollabImportedWfId = selectedWf.id;
          if (isWorkflowCollabEnabled()) {
            const joinPromise: Promise<void> = this.props.sd.joinWorkflowCollabSession(selectedWf.id);
            this.installCollabOverlay(container);
            // Heal AFTER the join so the pool resize replicates as a
            // "/participantBounds" op (and lands in the server flush) instead of
            // silently diverging this canvas from the session doc. Fire-and-forget:
            // the heal must not hold updateWorkFlow's modeler op lock through the
            // join round-trip.
            void joinPromise.finally(() => this.healPoolLaneGeometry(selectedWf.id));
          } else {
            this.healPoolLaneGeometry(selectedWf.id);
          }
        } catch (error) {
          console.error("Error importing XML:", error);
          this.props.sd.setCreatingWfActive(true);
          this.setDisable();
        }
      } else {
        this.props.sd.setCreatingWfActive(true);
        this.setDisable();
      }
    } else {
      this.props.sd.setCreatingWfActive(true);
      this.setDisable();
    }
  };

  /**
   * KOA-18258 load-time repair: workflows persisted before the participant-bounds collab
   * vocabulary carry lane bands that outgrew a pool frozen at an old session seed —
   * rendered as a phantom "extra lane" the user cannot delete. Grow the pool (through
   * modeling, so a live collab session replicates it as a "/participantBounds" op and the
   * flush persists the healed XML) to cover the lane-band union. Idempotent: a consistent
   * diagram computes null and no command fires, so clean opens never dirty the workflow.
   */
  private healPoolLaneGeometry = (wfId: string): void => {
    try {
      const modeler: IHealModelerView | undefined = this.modeler as IHealModelerView | undefined;
      // Guard against modeler swaps/imports that landed while a collab join was awaited.
      if (!modeler || modeler.__wfCollabImportedWfId !== wfId) return;
      if (this.computeReadOnly(this.props.sd.selectedWf)) return;
      const elementRegistry: IHealElementRegistry = modeler.get("elementRegistry") as IHealElementRegistry;
      const participant: IHealRegistryShape | undefined = elementRegistry.filter(
        (element: IHealRegistryShape): boolean => element.type === "bpmn:Participant"
      )[0];
      if (!participant) return;
      const poolRect: IPoolLaneRect | null = healRectOf(participant);
      if (!poolRect) return;
      const lanes: IPoolLaneRect[] = elementRegistry
        .filter((element: IHealRegistryShape): boolean => element.type === "bpmn:Lane")
        .map(healRectOf)
        .filter((rect: IPoolLaneRect | null): rect is IPoolLaneRect => rect !== null);
      const isHorizontal: boolean = participant.di?.isHorizontal !== false;
      const healed: IPoolLaneRect | null = healedPoolBounds(poolRect, lanes, isHorizontal);
      if (!healed) return;
      const modeling: IHealModeling = modeler.get("modeling") as IHealModeling;
      modeling.resizeShape(participant, healed);
    } catch (error) {
      // A failed repair must never break opening the workflow — the user still sees
      // the (corrupt but editable) diagram, exactly as before this heal existed.
      console.error("pool/lane geometry heal failed:", error);
    }
  };

  setDisable = () => {
    if (!this.modeler) return;
    (this.modeler as any)._container.style.pointerEvents = "none";
    (this.modeler as any)._container.style.opacity = "0.2";
  };

  zoomIn = () => {
    if (!this.modeler) return;
    (this.modeler.get("zoomScroll") as any).stepZoom(0.5);
  };

  zoomOut = () => {
    if (!this.modeler) return;
    (this.modeler.get("zoomScroll") as any).stepZoom(-0.5);
  };

  // Fit & centre the whole diagram in the viewport. Replaces the old zoom-reset-to-100%, which
  // was wired to the "expand/fit" icon but only reset the scale without fitting or recentring.
  fitDiagram = () => {
    if (!this.modeler) return;
    this.modeler.get("canvas").zoom("fit-viewport", "auto");
  };

  toogleMiniMap = (visible: boolean) => {
    if (!this.modeler) return;
    if (!visible) (this.modeler.get("minimap") as any).open();
    else (this.modeler.get("minimap") as any).close();
    this.setState({ miniMapVisible: !visible });
  };

  toggleLinting = () => {
    if (!this.modeler) return;

    try {
      const m: any = this.modeler;
      const linting = m.get?.("linting");
      if (!linting) return;

      const curr = typeof linting.isActive === "function" ? !!linting.isActive() : false;
      const next = !curr;

      if (next) {
        if (typeof linting.activate === "function") {
          linting.activate();
        } else if (typeof linting.toggle === "function" && !curr) {
          linting.toggle();
        }

        try {
          if (typeof linting.update === "function") {
            linting.update();
          }
        } catch {}
      } else if (typeof linting.deactivate === "function") {
        linting.deactivate();
      } else if (typeof linting.toggle === "function" && curr) {
        linting.toggle();
      }

      m.__kuika_lint_forced_off = !next;

      this.setState({
        lintingActive: next,
        lintErrors: next ? this.state.lintErrors : 0,
        lintWarnings: next ? this.state.lintWarnings : 0
      });

      if (!next) {
        const container: HTMLElement | undefined = m._container;
        if (container) {
          container.querySelectorAll<HTMLElement>(".djs-overlay.djs-overlay-linting").forEach((el) => el.remove());
        }
      }
    } catch {}
  };

  /**
   * One step of the diagram's history, in either direction — the single implementation behind
   * both Ctrl+Z / Ctrl+Y and the sub-bar's buttons.
   *
   * With OTHER people on this canvas the modeler's command stack is not ours alone to rewind:
   * undoing it would revert their remotely applied commands, so the per-user inverse-op stack
   * owns the history instead. Alone it must NOT — and that distinction is the fix for
   * KOA-15565. The gate used to be "is a collab session live", which is true for a lone editor
   * too (the session opens whether or not anyone joins), so every solo undo went to the op
   * stack; that stack rewinds the workflow DOCUMENT and never touches the diagram, leaving a
   * deleted shape deleted and the command stack untouched — an undo that looked dead.
   * `hasOtherWorkflowParticipants` draws the same line screen editing has always drawn.
   */
  stepCanvasHistory = async (direction: WorkflowHistoryDirection): Promise<void> => {
    const modeler: IHealModelerView | undefined = this.modeler as IHealModelerView | undefined;
    if (!modeler) return;

    if (this.props.sd.hasOtherWorkflowParticipants()) {
      if (direction === "undo") {
        this.props.sd.undoWorkflowLocalOp();
      } else {
        this.props.sd.redoWorkflowLocalOp();
      }
      this.publishCanvasHistoryState();
      return;
    }

    const editorActions: IWfEditorActions = modeler.get("editorActions") as IWfEditorActions;
    editorActions.trigger(direction);
    // bpmn-js returns from the trigger before the re-render settles, and the rebuild reads the
    // element registry — so it has to let that land first. Carried over from the key handler.
    await new Promise<void>((resolve): void => {
      setTimeout(resolve, 250);
    });
    await this.rebuildWorkflowDataFromDiagram();
    this.publishCanvasHistoryState();
  };

  /**
   * Tell the sub-bar's buttons what the AUTHORITATIVE stack can do right now — the same stack
   * `stepCanvasHistory` would act on, so the buttons can never offer a step that would land
   * somewhere else. Published on every command-stack change and after each step.
   */
  publishCanvasHistoryState = (): void => {
    const modeler: IHealModelerView | undefined = this.modeler as IHealModelerView | undefined;
    if (!modeler) {
      publishWorkflowHistoryState(IDLE_WORKFLOW_HISTORY);
      return;
    }

    if (this.props.sd.hasOtherWorkflowParticipants()) {
      publishWorkflowHistoryState({
        canUndo: this.props.sd.canUndoWorkflowCollab(),
        canRedo: this.props.sd.canRedoWorkflowCollab()
      });
      return;
    }

    try {
      const commandStack: IWfCommandStack = modeler.get("commandStack") as IWfCommandStack;
      publishWorkflowHistoryState({ canUndo: commandStack.canUndo(), canRedo: commandStack.canRedo() });
    } catch {
      publishWorkflowHistoryState(IDLE_WORKFLOW_HISTORY);
    }
  };

  /**
   * Rebuilds the workflow model from whatever the modeler currently holds — XML, node lists
   * and the cached per-node data the diagram does not itself carry. Every gesture that moves
   * the diagram behind the model's back (undo, redo, delete, paste) has to run it afterwards,
   * and those now arrive from the sub-bar's history buttons as well as from the keyboard —
   * hence a method rather than a closure inside the key handler.
   */
  rebuildWorkflowDataFromDiagram = async (): Promise<IWorkFlow | null> => {
    const modeler: IHealModelerView | undefined = this.modeler as IHealModelerView | undefined;
    if (!modeler) return null;

    const eventBus: IWfEventBus = modeler.get("eventBus") as IWfEventBus;
    const elementRegistry: IWfElementRegistry = modeler.get("elementRegistry") as IWfElementRegistry;
    const selection: IWfSelection = modeler.get("selection") as IWfSelection;
    const modeling: IWfModeling = modeler.get("modeling") as IWfModeling;
    if (!modeler.__fullDataCache) {
      modeler.__fullDataCache = new Map<string, Partial<IWFNode>>();
    }
    const dataCache: Map<string, Partial<IWFNode>> = modeler.__fullDataCache;

    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    if (!wf) return null;

    try {
      const { xml }: SaveXMLResult = await modeler.saveXML({ format: true });
      if (xml) wf.xml = xml;

      if (!wf.nodes) {
        // description/dueIn are unused by the frontend rebuild path — omitted here exactly as the
        // pre-refactor object literal omitted them, so this stays behavior-identical.
        wf.nodes = {
          steps: [],
          events: [],
          gateways: [],
          lanes: [],
          forwards: [],
          decisions: []
        } as unknown as IWFNodes;
      } else {
        wf.nodes.steps = [];
        wf.nodes.events = [];
        wf.nodes.gateways = [];
        wf.nodes.lanes = [];
        wf.nodes.forwards = [];
        wf.nodes.decisions = [];
      }

      const nodes: IWFNodes = wf.nodes!;
      const allElements: IWfDiagramElement[] = elementRegistry.getAll();

      const typePriority: Record<string, number> = {
        "bpmn:Lane": 1,
        "bpmn:SubProcess": 2,
        "bpmn:StartEvent": 3,
        "bpmn:EndEvent": 3,
        "bpmn:Task": 3,
        "bpmn:UserTask": 3,
        "bpmn:SequenceFlow": 4
      };
      const sortedElements: IWfDiagramElement[] = allElements.sort((a: IWfDiagramElement, b: IWfDiagramElement) => {
        const typeA: string = a.businessObject?.$type ?? "";
        const typeB: string = b.businessObject?.$type ?? "";
        return (typePriority[typeA] || 99) - (typePriority[typeB] || 99);
      });

      sortedElements.forEach((el: IWfDiagramElement) => {
        if (el.type === "label" || el.type === "bpmn:Process" || el.type === "bpmn:Collaboration") return;
        if (!el.businessObject) return;

        if (dataCache.has(el.id)) {
          const cachedItem: Partial<IWFNode> = dataCache.get(el.id)!;
          if (cachedItem.name && (!el.businessObject.name || el.businessObject.name === "")) {
            modeling.updateProperties(el, { name: cachedItem.name });
          }
        }

        this.workflowSyncService.syncElement(el, wf);
      });

      const restoreFullData = (list: IWFNode[] | undefined): void => {
        if (!list) return;
        for (let i = 0; i < list.length; i++) {
          const currentItem: IWFNode = list[i];

          if (dataCache.has(currentItem.id)) {
            const cachedItem: Partial<IWFNode> = dataCache.get(currentItem.id)!;

            const keys = Object.keys(cachedItem);
            keys.forEach((key) => {
              if (key !== "id" && key !== "xml" && key !== "fromNodeId" && key !== "toNodeId") {
                try {
                  currentItem[key] = JSON.parse(JSON.stringify(cachedItem[key]));
                } catch (e) {
                  currentItem[key] = cachedItem[key];
                }
              }
            });

            if (cachedItem.name) {
              currentItem.name = cachedItem.name;
            }
          }
        }
      };

      if (nodes.steps) restoreFullData(nodes.steps);
      if (nodes.events) restoreFullData(nodes.events);
      if (nodes.gateways) restoreFullData(nodes.gateways);
      if (nodes.lanes) restoreFullData(nodes.lanes);
      if (nodes.decisions) restoreFullData(nodes.decisions);
      if (nodes.forwards) restoreFullData(nodes.forwards);

      eventBus.fire("elements.changed", { elements: allElements });

      const currentSelection: IWfDiagramElement[] = selection.get();
      eventBus.fire("selection.changed", { newSelection: [], oldSelection: [] });
      if (currentSelection.length > 0) {
        eventBus.fire("selection.changed", { newSelection: currentSelection, oldSelection: [] });
      }
    } catch (err: unknown) {}
    return wf;
  };

  handlePlatformKeyCombinations = async (e: any) => {
    const tagName = e.target?.tagName?.toUpperCase();
    const isContentEditable = e.target?.contentEditable === "true" || e.target?.isContentEditable === true;
    const isInMonacoEditor = e.target?.closest?.(".monaco-editor") !== null;
    const isInModal: boolean = isInsideModalOverlay(e.target);

    if (tagName === "INPUT" || tagName === "TEXTAREA" || isContentEditable || isInMonacoEditor || isInModal) {
      return;
    }

    const kc = CommonHelperFunctions.getKeyCombinationsFromKeysForDesignWindows(e);
    if (kc === undefined) return;

    const modeler: any = this.modeler;
    if (!modeler) return;

    const editorActions = modeler.get("editorActions");
    const eventBus = modeler.get("eventBus");
    const elementRegistry = modeler.get("elementRegistry");

    if (!(modeler as any).__fullDataCache) {
      (modeler as any).__fullDataCache = new Map<string, any>();
    }
    const dataCache = (modeler as any).__fullDataCache as Map<string, any>;

    const captureState = () => {
      const wf = this.props.sd.selectedWf;
      if (!wf || !wf.nodes) return;

      const allLists = [
        wf.nodes.steps,
        wf.nodes.events,
        wf.nodes.gateways,
        wf.nodes.lanes,
        wf.nodes.decisions,
        wf.nodes.forwards
      ];

      allLists.forEach((list) => {
        if (Array.isArray(list)) {
          list.forEach((item) => {
            if (item && item.id) {
              try {
                const clone = JSON.parse(JSON.stringify(item));
                dataCache.set(item.id, clone);
              } catch (err) {}
            }
          });
        }
      });

      const allElements = elementRegistry.getAll();
      allElements.forEach((el: any) => {
        if (el.businessObject && el.businessObject.name) {
          const existing = dataCache.get(el.id) || {};
          existing.name = el.businessObject.name;
          dataCache.set(el.id, existing);
        }
      });
    };

    if (!(modeler as any).__isDataListenerAttached) {
      eventBus.on("element.changed", () => captureState());
      eventBus.on("selection.changed", () => captureState());
      eventBus.on("commandStack.changed", () => captureState());

      captureState();
      (modeler as any).__isDataListenerAttached = true;
    }

    // Same routine the sub-bar's history buttons use — see the method for why it moved out.
    const rebuildWorkflowData = this.rebuildWorkflowDataFromDiagram;

    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    switch (kc) {
      case KeyCombinations.ctrlS:
        e.preventDefault();
        captureState();
        const savedWf = await rebuildWorkflowData();
        if (savedWf) {
          const result = await this.props.sd.saveWorkFlow(savedWf);
          if (result) KMainFunctions.displaySuccessNotification(`Process: ${savedWf.name} has been saved!`);
        }
        break;

      case KeyCombinations.ctrlZ:
        e.preventDefault();
        await this.stepCanvasHistory("undo");
        break;

      case KeyCombinations.ctrlY:
        e.preventDefault();
        await this.stepCanvasHistory("redo");
        break;

      case KeyCombinations.delete:
        captureState();
        e.preventDefault();
        editorActions.trigger("removeSelection");
        await rebuildWorkflowData();
        break;

      case KeyCombinations.ctrlC:
        e.preventDefault();
        editorActions.trigger("copy");
        break;

      case KeyCombinations.ctrlV:
        e.preventDefault();
        editorActions.trigger("paste");
        await wait(150);
        await rebuildWorkflowData();
        break;

      case KeyCombinations.ctrlF:
        e.preventDefault();
        editorActions.trigger("find");
        break;

      default:
        break;
    }
  };

  patchLabelEditingBBox(modeler: any) {
    const injector = modeler.get("injector");
    const canvas = modeler.get("canvas");
    const lep: any = injector.get("labelEditingProvider", false) || injector.get("bpmnLabelEditingProvider", false);
    if (!lep || typeof lep.getEditingBBox !== "function") return;

    const baseGet = lep.getEditingBBox.bind(lep);

    // Control buttons
    const CFG = {
      PAD_X: 5,
      PAD_Y: 3,
      FILL_W: 1.0,
      FILL_H: 0.74,
      MIN_W: 120,
      MIN_H: 28,
      CENTER_H: true,
      TOP_ALIGN: false,
      BASE_FONT_PX: 12,
      LINE_HEIGHT: 1.2
    } as const;

    const toOverlay = (x: number, y: number, w: number, h: number) => {
      const vb = canvas.viewbox();
      const s = vb.scale || 1;
      return {
        x: Math.round((x - vb.x) * s),
        y: Math.round((y - vb.y) * s),
        width: Math.round(w * s),
        height: Math.round(h * s),
        scale: s
      };
    };

    lep.getEditingBBox = (element: any) => {
      const bo = element?.businessObject;

      if (
        bo &&
        (is(bo, "bpmn:Task") || is(bo, "bpmn:SubProcess") || is(bo, "bpmn:CallActivity")) &&
        !element.labelTarget
      ) {
        const maxW = Math.max(10, Math.floor(element.width - 2 * CFG.PAD_X));
        const maxH = Math.max(10, Math.floor(element.height - 2 * CFG.PAD_Y));

        const wantW = Math.min(maxW, Math.max(CFG.MIN_W, Math.round(maxW * CFG.FILL_W)));
        const wantH = Math.min(maxH, Math.max(CFG.MIN_H, Math.round(maxH * CFG.FILL_H)));

        const x = Math.round(element.x + CFG.PAD_X + (CFG.CENTER_H ? (maxW - wantW) / 2 : 0));
        const y = Math.round(element.y + CFG.PAD_Y + (CFG.TOP_ALIGN ? 0 : (maxH - wantH) / 2));

        const ov = toOverlay(x, y, wantW, wantH);
        const ovMax = toOverlay(element.x + CFG.PAD_X, element.y + CFG.PAD_Y, maxW, maxH);

        const fontPx = Math.max(10, Math.round(CFG.BASE_FONT_PX * ov.scale));
        const lhPx = Math.round(fontPx * CFG.LINE_HEIGHT);

        return {
          bounds: { x: ov.x, y: ov.y, width: ov.width, height: ov.height },
          style: {
            minWidth: Math.round(CFG.MIN_W * ov.scale),
            maxWidth: ovMax.width,
            minHeight: Math.round(CFG.MIN_H * ov.scale),
            maxHeight: ovMax.height,
            fontSize: `${fontPx}px`,
            lineHeight: `${lhPx}px`
          }
        };
      }

      return baseGet(element);
    };
  }

  patchHideUserIconWhileEditing(modeler: any) {
    const eventBus = modeler.get("eventBus");
    const canvas = modeler.get("canvas");

    const getElement = (e: any) =>
      e?.active?.element || e?.context?.element || e?.element || e?.active?.context?.element || null;

    const setUserIconVisible = (element: any, visible: boolean) => {
      try {
        const gfx = canvas.getGraphics(element);
        if (!gfx) return;

        // Tag-less attribute selector: the icon used to be a single <image>, now it's a <g> of
        // two <path>s (see moddle/customRenderer.js) — this survives either element type.
        const icon = gfx.querySelector('[data-kuika-usericon="1"]') as SVGElement | null;
        if (!icon) return;

        (icon as any).style.visibility = visible ? "visible" : "hidden";
      } catch {}
    };

    const onActivate = (e: any) => {
      const el = getElement(e);
      if (!el) return;
      if (el.type !== "bpmn:UserTask") return;
      setUserIconVisible(el, false);
    };

    const onDeactivate = (e: any) => {
      const el = getElement(e);
      if (!el) return;
      if (el.type !== "bpmn:UserTask") return;
      setUserIconVisible(el, true);
    };

    eventBus.on("directEditing.activate", onActivate);
    eventBus.on("directEditing.deactivate", onDeactivate);
    eventBus.on("directEditing.complete", onDeactivate);
  }

  // for handling zoom in and zoom out
  private attachDirectEditingRepositionOnZoom(modeler: any): () => void {
    const eventBus = modeler.get("eventBus");
    const directEditing: any = modeler.get("directEditing", false);
    const injector = modeler.get("injector");

    const lep: any = injector.get("labelEditingProvider", false) || injector.get("bpmnLabelEditingProvider", false);

    if (!directEditing || !lep) return () => {};

    const doUpdate = () => {
      if (!directEditing.isActive || !directEditing.isActive()) return;
      const ctx = (directEditing as any)._ctx;
      const element = ctx?.element;
      if (!element) return;

      const bbox = lep.getEditingBBox(element);

      try {
        (directEditing as any)._textbox?.update(bbox);
      } catch {
        try {
          (directEditing as any).update?.();
        } catch {}
      }
    };

    let raf: number | null = null;
    const schedule = () => {
      if (raf !== null) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(doUpdate);
    };

    eventBus.on("canvas.viewbox.changing", schedule);
    eventBus.on("canvas.viewbox.changed", schedule);
    eventBus.on("shape.changed", schedule);
    eventBus.on("element.changed", schedule);

    // disposer
    return () => {
      eventBus.off("canvas.viewbox.changing", schedule);
      eventBus.off("canvas.viewbox.changed", schedule);
      eventBus.off("shape.changed", schedule);
      eventBus.off("element.changed", schedule);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }

  private removeOldLintStatusOverlay(modeler: any): () => void {
    if (!modeler) return () => {};

    const container: HTMLElement | undefined = (modeler as any)._container;
    if (!container) return () => {};

    const removeStatusNodes = () => {
      const all = Array.from(container.querySelectorAll<HTMLElement>("*"));
      all.forEach((el) => {
        const cls = typeof el.className === "string" ? el.className : "";
        if (!cls) return;

        if (cls.includes("bjsl-button")) {
          if (el.parentNode) {
            el.parentNode.removeChild(el);
          }
        }
      });
    };

    removeStatusNodes();

    let observer: MutationObserver | undefined;
    if (typeof MutationObserver !== "undefined") {
      observer = new MutationObserver((mutations) => {
        let hasAdded = false;
        for (const m of mutations) {
          if (m.addedNodes && m.addedNodes.length > 0) {
            hasAdded = true;
            break;
          }
        }
        if (hasAdded) removeStatusNodes();
      });

      observer.observe(container, { childList: true, subtree: true });
    }

    return () => {
      try {
        observer?.disconnect();
      } catch {}
      try {
        removeStatusNodes();
      } catch {}
    };
  }

  // Themed hover tooltips for context-pad entries. Those entries carry a native `title`, which the
  // browser renders as an unstyled, slow tooltip. We show a token-styled tooltip instead: a single
  // shared element on <body> (so the pad never clips it), positioned via getBoundingClientRect
  // with a viewport-edge flip. The native `title` is stashed to a data attr while hovered
  // (suppressing the browser tooltip) and restored on leave so a11y/fallback survives.
  installEntryTooltips(modeler: any) {
    const container: HTMLElement | null = (modeler?._container as HTMLElement) || null;
    if (!container || typeof document === "undefined") return;

    // Context-pad only — the stock palette is hidden (the Kuika rail palette has its own tooltips).
    const SELECTOR = ".djs-context-pad .entry";
    const tip: HTMLElement = document.createElement("div");
    tip.className = "kuika-djs-entry-tip";
    document.body.appendChild(tip);

    const show = (entry: HTMLElement): void => {
      const title: string = entry.getAttribute("title") || entry.getAttribute("data-kuika-title") || "";
      if (!title) return;
      if (entry.hasAttribute("title")) {
        entry.setAttribute("data-kuika-title", title);
        entry.removeAttribute("title");
      }
      tip.textContent = title;
      tip.classList.add("is-visible");
      const r: DOMRect = entry.getBoundingClientRect();
      const tr: DOMRect = tip.getBoundingClientRect();
      let left: number = r.right + 8;
      if (left + tr.width > window.innerWidth - 8) left = r.left - tr.width - 8;
      let top: number = r.top + r.height / 2 - tr.height / 2;
      top = Math.max(8, Math.min(top, window.innerHeight - tr.height - 8));
      tip.style.left = `${left}px`;
      tip.style.top = `${top}px`;
    };

    const hide = (): void => {
      tip.classList.remove("is-visible");
    };
    const onOver = (e: Event): void => {
      const target = e.target as HTMLElement | null;
      const entry = target?.closest?.(SELECTOR) as HTMLElement | null;
      if (entry) show(entry);
    };
    const onOut = (e: Event): void => {
      const target = e.target as HTMLElement | null;
      const entry = target?.closest?.(SELECTOR) as HTMLElement | null;
      if (!entry) return;
      const saved: string | null = entry.getAttribute("data-kuika-title");
      if (saved && !entry.hasAttribute("title")) entry.setAttribute("title", saved);
      hide();
    };

    container.addEventListener("mouseover", onOver);
    container.addEventListener("mouseout", onOut);
    // A clicked entry can REMOVE itself (e.g. "Remove" deletes the element and the pad with it) —
    // then no mouseout ever arrives from the detached node and the tip would stay stuck on screen
    // (KOA-18275). Hide eagerly on any press, on the pad closing, and on the pointer leaving the
    // canvas; the next hover re-shows it.
    container.addEventListener("pointerdown", hide, true);
    container.addEventListener("mouseleave", hide);
    const eventBus = modeler?.get?.("eventBus");
    try {
      eventBus?.on?.("contextPad.close", hide);
    } catch {}

    this.disposeEntryTooltips = () => {
      container.removeEventListener("mouseover", onOver);
      container.removeEventListener("mouseout", onOut);
      container.removeEventListener("pointerdown", hide, true);
      container.removeEventListener("mouseleave", hide);
      try {
        eventBus?.off?.("contextPad.close", hide);
      } catch {}
      try {
        tip.remove();
      } catch {}
    };
  }

  // Close the context pad (and drop the stale selection) the moment its target element is
  // removed. diagram-js's own `elements.changed` handler RE-OPENS the pad whenever its target
  // is in the change set (ContextPad.js) — and a delete puts the removed node in that set, so
  // the pad re-opens on a node that no longer exists and is left orphaned on an empty spot,
  // with the properties panel still showing the deleted node. `shape.remove` / `connection.remove`
  // fire during the delete command, BEFORE that `elements.changed`, so closing here nulls the
  // pad's `_current` and the subsequent re-open no-ops.
  installContextPadAutoClose(modeler: any): (() => void) | undefined {
    const m: any = modeler;
    const eventBus = m?.get?.("eventBus");
    // Non-strict: prepareModeller also runs on the read-only NavigatedViewer, which has no
    // contextPad at all. A strict get throws there, and the throw kills the whole build.
    const contextPad = m?.get?.("contextPad", false);
    const selection = m?.get?.("selection");
    const canvas = m?.get?.("canvas");
    if (!eventBus || !contextPad) return undefined;

    const onElementRemove = (e: any): void => {
      const removed = e?.element;
      if (!removed) return;

      // Drop the removed element from the selection so the properties panel doesn't keep
      // showing a node that is gone.
      try {
        if (selection?.get?.().includes(removed)) selection.deselect(removed);
      } catch {}

      // Close if the pad targets the removed element (single or multi-target).
      try {
        if (!contextPad.isOpen?.()) return;
        const target = contextPad._current?.target;
        const targets = Array.isArray(target) ? target : target ? [target] : [];
        if (targets.includes(removed)) contextPad.close();
      } catch {}
    };

    // Selecting the diagram root (Collaboration) or a label is the user clicking away, not a
    // meaningful target, so close the pad. Registered below diagram-js's own selection.changed
    // handler (priority 1000) so it runs AFTER the open, in the same event dispatch — no visible
    // flash. NB: Lanes and the Participant (pool) DO have their own useful pad actions (add lane
    // above/below, delete lane, delete pool), so they are NOT treated as background — their pads
    // must show. bpmn:Group (Happy Path phase) likewise stays actionable (delete).
    const BACKGROUND_TYPES: Set<string> = new Set(["bpmn:Collaboration"]);
    const onSelectionChanged = (e: any): void => {
      try {
        if (!contextPad.isOpen?.()) return;
        const sel: any[] = e?.newSelection || [];
        if (sel.length !== 1) return;
        const only = sel[0];
        const isBackground = only?.type === "label" || BACKGROUND_TYPES.has(only?.businessObject?.$type);
        if (isBackground) contextPad.close();
      } catch {}
    };

    // Triggering the pad's "connect" action (draw a sequence flow) starts an interactive connect —
    // the user is now dragging a flow to a target, so the pad should get out of the way. diagram-js
    // leaves it open, so close it explicitly. We listen to `connect.init` (fired immediately when
    // connect is initiated) rather than only `connect.start`, because `connect.start` fires ONLY
    // after the drag threshold is crossed — a quick click / tiny movement skips it, which made the
    // close intermittent. `connect.start` is kept as a belt-and-suspenders fallback; the handler is
    // idempotent (guarded by isOpen).
    const onConnectStart = (): void => {
      try {
        if (contextPad.isOpen?.()) contextPad.close();
      } catch {}
    };

    // Click-anchored pad placement. diagram-js anchors a pad at the target's bbox top-right (last
    // waypoint for connections). That's fine for normal nodes, but poor for elements whose bbox is
    // huge or off to the side: a connection's arrowhead sits far from where the line was clicked,
    // and a Lane / Participant (pool) spans the whole diagram so its top-right corner lands off in
    // the canvas — often under the right-side properties panel. For those element types, re-anchor
    // the pad at the actual click point so it always opens right next to where the user clicked.
    const shouldAnchorToClick = (el: any): boolean => {
      if (!el) return false;
      if (Array.isArray(el.waypoints)) return true; // connection
      const type: string | undefined = el.businessObject?.$type;
      return type === "bpmn:Lane" || type === "bpmn:Participant";
    };
    interface PadClickPoint {
      element: any;
      x: number;
      y: number;
    }
    let lastPadClick: PadClickPoint | null = null;
    // diagram-js's `element.click` payload is only { element, gfx, originalEvent } — no diagram
    // coordinates — so convert the native pointer position into diagram space using the root
    // layer's screen CTM (which already bakes in pan/zoom). Inverting it maps client px → diagram
    // coordinates, the space `_getPosition` returns.
    const onElementClick = (e: any): void => {
      const el: any = e?.element;
      const originalEvent: any = e?.originalEvent;
      if (!shouldAnchorToClick(el) || !originalEvent || !canvas?.getRootElement || !canvas?.getGraphics) {
        lastPadClick = null;
        return;
      }
      try {
        const rootGfx: any = canvas.getGraphics(canvas.getRootElement());
        const svg: any = rootGfx?.ownerSVGElement;
        const ctm: any = rootGfx?.getScreenCTM?.();
        if (!svg?.createSVGPoint || !ctm) {
          lastPadClick = null;
          return;
        }
        const point: any = svg.createSVGPoint();
        point.x = originalEvent.clientX;
        point.y = originalEvent.clientY;
        const local: any = point.matrixTransform(ctm.inverse());
        lastPadClick = { element: el, x: local.x, y: local.y };
      } catch {
        lastPadClick = null;
      }
    };

    const PAD_CLICK_PADDING = 12; // matches diagram-js CONTEXT_PAD_PADDING
    const patchedGetPosition: boolean = typeof contextPad._getPosition === "function";
    const originalGetPosition: ((target: any) => any) | undefined = patchedGetPosition
      ? contextPad._getPosition.bind(contextPad)
      : undefined;
    if (originalGetPosition) {
      contextPad._getPosition = (target: any): any => {
        const clickMatches: boolean =
          Boolean(lastPadClick) && lastPadClick?.element === target && shouldAnchorToClick(target);
        if (clickMatches && lastPadClick) {
          return {
            position: {
              left: lastPadClick.x + PAD_CLICK_PADDING,
              top: lastPadClick.y - PAD_CLICK_PADDING / 2
            }
          };
        }
        return originalGetPosition(target);
      };
    }

    eventBus.on(["shape.remove", "connection.remove"], onElementRemove);
    eventBus.on("selection.changed", 500, onSelectionChanged);
    eventBus.on(["connect.init", "connect.start"], onConnectStart);
    // High priority (> the default 1000) so the click point is captured BEFORE diagram-js's own
    // selection handler runs and synchronously opens the pad (which reads `_getPosition`).
    eventBus.on("element.click", 2000, onElementClick);
    return () => {
      try {
        eventBus.off(["shape.remove", "connection.remove"], onElementRemove);
        eventBus.off("selection.changed", onSelectionChanged);
        eventBus.off(["connect.init", "connect.start"], onConnectStart);
        eventBus.off("element.click", onElementClick);
        // Restore diagram-js's own prototype positioning.
        if (patchedGetPosition) delete contextPad._getPosition;
      } catch {}
    };
  }

  installDiagramJsTooltipBridge(modeler: any) {
    const m: any = modeler;
    const container: HTMLElement | null = (m?._container as HTMLElement) || null;
    if (!container) return;

    if ((container as any).__kuika_tip_bridge_installed) return;
    (container as any).__kuika_tip_bridge_installed = true;

    let raf: number | null = null;
    const scan = () => {
      raf = null;
      const tips = container.querySelectorAll<HTMLElement>(
        ".djs-tooltip.djs-tooltip-error, .djs-tooltip.djs-tooltip-warning"
      );
      tips.forEach(decorateRuntimeLintTip);
    };
    const scheduleScan = () => {
      if (raf !== null) return;
      raf = requestAnimationFrame(scan);
    };

    scheduleScan();

    const obs = new MutationObserver(() => scheduleScan());
    obs.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["class", "style"]
    });

    (container as any).__kuika_runtime_tip_bridge_obs = obs;
  }

  uninstallDiagramJsTooltipBridge(modeler: any) {
    const m: any = modeler;
    const container: HTMLElement | null = (m?._container as HTMLElement) || null;
    if (!container) return;

    try {
      (container as any).__kuika_runtime_tip_bridge_obs?.disconnect?.();
    } catch {}

    try {
      const el = (container as any).__kuika_runtime_tip_el as HTMLElement | null;
      if (el && el.parentNode) el.parentNode.removeChild(el);
    } catch {}

    const t = (container as any).__kuika_runtime_tip_timer as number | undefined;
    if (t) window.clearTimeout(t);

    (container as any).__kuika_runtime_tip_bridge_obs = null;
    (container as any).__kuika_runtime_tip_el = null;
    (container as any).__kuika_runtime_tip_timer = undefined;
    (container as any).__kuika_tip_bridge_installed = false;
  }

  render() {
    const { isDiagramLoading } = this.state;
    const { lintErrors, lintWarnings, lintingActive } = this.state;

    let lintLabel: string;
    if (!lintingActive) {
      lintLabel = "Show Errors and Warnings";
    } else {
      lintLabel = `${lintErrors} errors, ${lintWarnings} warnings`;
    }

    let lintColor: string | undefined;
    if (lintingActive) {
      if (lintErrors) {
        lintColor = "var(--red-6)";
      } else if (lintWarnings > 0) {
        lintColor = "var(--yellow-6)";
      } else {
        lintColor = undefined;
      }
    }

    const selectedWf = this.props.sd.selectedWf;
    // "Empty" = a process with no user steps and no gateways (just the mandatory Start/End). Used to
    // show a guidance hint over the otherwise-bare canvas; pointer-events:none keeps it click-through.
    const stepCount: number = selectedWf?.nodes?.steps?.length ?? 0;
    const gatewayCount: number = selectedWf?.nodes?.gateways?.length ?? 0;
    const isCanvasEmpty: boolean = !!selectedWf && stepCount === 0 && gatewayCount === 0;
    return (
      this.props?.sd && (
        <>
          {/* The editor chrome (switcher + tabs) sits above the canvas; its non-Diagram
              tabs render as overlays so the bpmn-js instance is never unmounted. */}
          <div className="kuika-process-editor">
            {!this.props.sd.isCreateWfActive && <ProcessEditor />}
            <div className="modeler-parent">
              {/* Inside `.modeler-parent` (position: relative) so the gallery can fill
                it absolutely and scroll internally instead of spilling past the column. */}
              {this.props.sd.isCreateWfActive && <CreateWorkflow />}
              {/* Diagram-load feedback for the modeler only. It used to render a raw
                "Loading..." span on an inline-styled scrim that also sat over the
                create-process gallery, which owns its own table spinner. */}
              {isDiagramLoading && !this.props.sd.isCreateWfActive && (
                <div className="kuika-workflow-modeler__loading">
                  <KuikaSmallSpinner spinning />
                </div>
              )}
              <div ref={this.modelerRef} id="modeler-container"></div>
              {!this.props.sd.isCreateWfActive && !isDiagramLoading && <DiagramPalette modeler={this.modeler} />}
              {!this.props.sd.isCreateWfActive && !isDiagramLoading && isCanvasEmpty && (
                <div className="kuika-wf-empty-hint" aria-hidden="true">
                  <KuikaSvg name="bulb" width={26} height={26} color="var(--dark-5)" />
                  <div className="kuika-wf-empty-hint__title">This process is empty</div>
                  <div className="kuika-wf-empty-hint__sub">Drag an element from the palette to add a step.</div>
                </div>
              )}
              {!this.props.sd.isCreateWfActive && (
                <div className="kuika-wf-diagram-toolbar" role="toolbar" aria-label="Diagram controls">
                  <div className="kuika-wf-diagram-toolbar__group">
                    <KuikaTooltip content="Zoom out">
                      <KuikaButton type="text" isIcon aria-label="Zoom out" onClick={() => this.zoomOut()}>
                        <KuikaSvg name="zoom-out" width={16} height={16} color="var(--dark-7)" />
                      </KuikaButton>
                    </KuikaTooltip>
                    <KuikaTooltip content="Zoom in">
                      <KuikaButton type="text" isIcon aria-label="Zoom in" onClick={() => this.zoomIn()}>
                        <KuikaSvg name="zoom-in" width={16} height={16} color="var(--dark-7)" />
                      </KuikaButton>
                    </KuikaTooltip>
                    <KuikaTooltip content="Fit to screen">
                      <KuikaButton type="text" isIcon aria-label="Fit to screen" onClick={() => this.fitDiagram()}>
                        <KuikaSvg name="maximize" width={16} height={16} color="var(--dark-7)" />
                      </KuikaButton>
                    </KuikaTooltip>
                  </div>
                  <span className="kuika-wf-diagram-toolbar__divider" />
                  <KuikaTooltip content={`${this.state.miniMapVisible ? "Hide" : "Show"} navigator`}>
                    <KuikaButton
                      type="text"
                      isIcon
                      active={this.state.miniMapVisible}
                      aria-label="Toggle navigator"
                      onClick={() => this.toogleMiniMap(this.state.miniMapVisible)}
                    >
                      <KuikaSvg
                        name="map"
                        width={16}
                        height={16}
                        color={this.state.miniMapVisible ? "var(--blue-5)" : "var(--dark-7)"}
                      />
                    </KuikaButton>
                  </KuikaTooltip>
                  <span className="kuika-wf-diagram-toolbar__divider" />
                  <KuikaTooltip content={lintLabel}>
                    <KuikaButton
                      type="text"
                      isIcon
                      active={lintingActive}
                      aria-label="Toggle problems"
                      onClick={this.toggleLinting}
                    >
                      <KuikaSvg name="alert-triangle" width={16} height={16} color={lintColor ?? "var(--dark-7)"} />
                    </KuikaButton>
                  </KuikaTooltip>
                </div>
              )}
              {!isWorkflowCollabEnabled() &&
                selectedWf?.userInUse !== undefined &&
                selectedWf?.userInUse !== null &&
                selectedWf?.userInUse !== this.currentUser && (
                  <KuikaInfoBox
                    color="blue"
                    leftUnit={
                      <KuikaText>Read Only! - This process is currently locked by [{selectedWf?.userInUse}]</KuikaText>
                    }
                    style={{ position: "fixed", top: 53, margin: "7px" }}
                  />
                )}
            </div>
          </div>
        </>
      )
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason, nextProps: IWorkFlowWindowProps & IWithSDProps): boolean => {
    // The window MUST re-render when the selected workflow changes so
    // componentDidUpdate fires and ensureModelerBuilt() self-heals the modeler.
    // PureSdComponent defers to checkNeedRender for non-whitelisted SD change
    // reasons; returning false unconditionally meant a chat-driven / programmatic
    // setSelectedWf that lands AFTER this canvas mounts (create_workflow
    // openAfterCreate navigates → mounts → then sets selectedWf) never
    // re-rendered — so componentDidUpdate never ran, window.bpmnModeler stayed
    // undefined, and every ui_workflow_dsl edit failed "BPMN Modeler is not
    // ready", leaving the process empty. (KOA-17362)
    const nextWfId: string | undefined = nextProps?.sd?.selectedWf?.id;
    const currentWfId: string | undefined = this.props?.sd?.selectedWf?.id;
    if (nextWfId !== currentWfId) return true;
    // Self-heal backstop: a workflow is selected but the modeler is not built yet
    // (mount/select race) — allow the render so ensureModelerBuilt can retry.
    if (nextWfId && !this.modeler && !this.buildingModeler) return true;
    return false;
  };
}

const workFlowWindow = withSD<IWorkFlowWindowProps>(WorkFlowWindow);
export { workFlowWindow as WorkFlowWindow };
